import { cloneDeep } from 'lodash';
import { useRef } from 'react';
import { IFramework, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { rotatePoint, translateYReverse } from '@/utils/math-kit';
import { ILayerProps } from '@/version-types';
import { useDeformationImgState } from '../states/deformation-img';
import { CornerPoint, RectangleAnchorPointName } from '../types';
import { moveLeftBottom, moveLeftTop, moveRightBottom, moveRightTop } from '../utils';
import { StageWidthScale } from './render-stage';

const DeformationImgOperate: React.FC<{
    scale: number;
    framework: IFramework;
    layer: ILayerProps;
    latestPoints: CornerPoint[];
    setLatestPoints: (points: CornerPoint[]) => void;
    maxWidth: number;
    maxHeight: number;
}> = ({ scale, layer, latestPoints, setLatestPoints, maxWidth, maxHeight }) => {
    const lastPoints = useRef<CornerPoint[]>(latestPoints);
    const currentType = useDeformationImgState()((state) => state.currentType);
    const setIsOperation = useDeformationImgState()((state) => state.setIsOperation);

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');
        if (
            dataType !== RectangleAnchorPointName.LEFT_TOP &&
            dataType !== RectangleAnchorPointName.RIGHT_TOP &&
            dataType !== RectangleAnchorPointName.RIGHT_BOTTOM &&
            dataType !== RectangleAnchorPointName.LEFT_BOTTOM
        ) {
            return;
        }
        setIsOperation(true);
        const { x: moveX, y: moveY } = translateYReverse(
            rotatePoint(translateYReverse({ x: e.deltaX / scale, y: e.deltaY / scale }), layer.rotate),
        );

        const newPoints = cloneDeep(lastPoints.current);
        if (dataType === RectangleAnchorPointName.LEFT_TOP) {
            moveLeftTop(newPoints, moveX, moveY, currentType, maxWidth, maxHeight);
        } else if (dataType === RectangleAnchorPointName.RIGHT_TOP) {
            moveRightTop(newPoints, moveX, moveY, currentType, maxWidth, maxHeight);
        } else if (dataType === RectangleAnchorPointName.RIGHT_BOTTOM) {
            moveRightBottom(newPoints, moveX, moveY, currentType, maxWidth, maxHeight);
        } else if (dataType === RectangleAnchorPointName.LEFT_BOTTOM) {
            moveLeftBottom(newPoints, moveX, moveY, currentType, maxWidth, maxHeight);
        }
        lastPoints.current = newPoints;
        setLatestPoints(newPoints);
        console.log('newPoints===', newPoints);
        if (e.isEnd) {
            setIsOperation(false);
        }
    });

    return (
        <div className="c-deformation-img-operate">
            <div
                style={{
                    width: layer.width * StageWidthScale,
                    height: layer.height * StageWidthScale,
                    transform: `scale(${scale})`,
                    transformOrigin: 'center center',
                }}
            >
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    {latestPoints.map((point) => {
                        return (
                            <div
                                className="c-deformation-img-operate-point"
                                data-operate-type={point.type}
                                key={point.type}
                                style={{
                                    position: 'absolute',
                                    top: point.top,
                                    left: point.left,
                                    width: `${10 / scale}px`,
                                    height: `${10 / scale}px`,
                                    filter: `drop-shadow(0px ${6 / scale}px ${24 / scale}px rgba(0, 0, 0, 0.06)) drop-shadow(0px 0px ${2 / scale}px rgba(0, 0, 0, 0.08))`,
                                    background: '#70CBFF',
                                    border: `${2 / scale}px solid #fff`,
                                    borderRadius: '50%',
                                    transform: `translate(-50%, -50%)`,
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                }}
                            ></div>
                        );
                    })}
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={layer.width * StageWidthScale}
                    height={layer.height * StageWidthScale}
                >
                    {latestPoints.map((point, index) => {
                        const isLast = index === latestPoints.length - 1;
                        const endPoints = isLast ? latestPoints[0] : latestPoints[index + 1];
                        return (
                            <line
                                key={point.type}
                                x1={point.left}
                                y1={point.top}
                                x2={endPoints.left}
                                y2={endPoints.top}
                                stroke="#70CBFF"
                                strokeWidth={`${2 / scale}px`}
                            />
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

export default DeformationImgOperate;
